import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { useOnScreen } from '@quality24/react-hooks';

import { Surface } from '@quality24/design-system';
import {
  Customizations,
  CustomViewData,
  GraphQLFilter,
  HasPermissionFn,
  TabbedFilterComponentData,
} from '../../@types';

import { useQueryFilter } from '../../hooks/filterHooks';

import {
  formatSearchFilterId,
  mergePermissionFilter,
} from '../../utils/filterUtils';
import styles from './CustomViewContainer.module.scss';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: CustomViewData;
  customizations?: Customizations;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
  hasPermission?: HasPermissionFn;
}

const CustomViewContainer: React.FunctionComponent<Props> = ({
  className,
  data: {
    title,
    outerClassName,
    className: configClassName,
    options,
    customComponentProps,
    generalFilters,
    specificFilters,
    filterModifiers,
    permissionFilters,
    customQueryFilter,
    permission,
    componentName,
  },
  customizations,
  baseFilter,
  permissionFilter: userPermissionFilter = {},
  hasPermission,
  ...htmlElementProps
}) => {
  // Create a component id
  const idRef = React.useRef(uuid());

  // Check if this component is visible on screen
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(elementRef);

  // Fetch filters
  const { getGraphQLFilter, getPermissionFilter } = useQueryFilter(
    [
      ...(generalFilters || []),

      // Find specific filters id (these should be parsed due to special search filter)
      ...(specificFilters?.flatMap((f) => {
        if (f.type === 'search') return formatSearchFilterId(f.id);
        if (f.type === 'tabbed') {
          return (f as TabbedFilterComponentData).components.map((c) => c.id);
        }
        return f.id;
      }) || []),
    ],
    idRef.current,
  );

  // Retrieve and memoize the graphQLFilters
  // This getter applies any usage modifications
  const graphQLFilter = React.useMemo(
    () => getGraphQLFilter(filterModifiers),
    [getGraphQLFilter, filterModifiers],
  );

  const filter = React.useMemo(() => {
    if (
      (baseFilter && Object.keys(baseFilter).length > 0) ||
      (graphQLFilter && Object.keys(graphQLFilter).length > 0) ||
      (permissionFilters && permissionFilters.length > 0) ||
      (customQueryFilter && Object.keys(customQueryFilter).length > 0)
    ) {
      const permissionFilter = getPermissionFilter(
        userPermissionFilter,
        permissionFilters,
      );

      const parsedFilters = mergePermissionFilter(
        {
          ...baseFilter,
          ...customQueryFilter,
          ...graphQLFilter,
        },
        permissionFilter,
      );

      return Object.keys(parsedFilters).length ? parsedFilters : undefined;
    }
    return undefined;
  }, [
    baseFilter,
    customQueryFilter,
    getPermissionFilter,
    graphQLFilter,
    permissionFilters,
    userPermissionFilter,
  ]);

  if (hasPermission && permission && !hasPermission(permission)) {
    return null;
  }

  return (
    <div
      ref={elementRef}
      className={classNames(className, outerClassName)}
      {...htmlElementProps}
    >
      <Surface
        className={classNames(styles.surface, {
          'on-surface': options?.renderOnSurface !== false,
        })}
        padding="sm"
        backgroundColor="grayLight1"
      >
        <Surface
          className={classNames(styles.surface, {
            'on-surface': options?.renderOnInnerSurface,
          })}
          padding="sm"
          backgroundColor="grayLight2"
        >
          {customizations?.customComponents &&
            customizations.customComponents[componentName] &&
            (() => {
              const Component = customizations.customComponents[
                componentName
              ] as typeof React.Component;
              return (
                // `title`/`specificFilters` are forwarded rather than rendered
                // here: a custom component's layout is arbitrary (unlike a
                // chart/table), so it — not this generic container — decides
                // where its own title and section-scoped filters belong. See
                // e.g. ScheduledTerminalChart (renders `title` itself) and
                // FleuryIndicatorAnalysis (renders `specificFilters` itself,
                // via `<FilterViewContainer partitionId={filterPartitionId} />`).
                <Component
                  title={title}
                  className={configClassName}
                  filters={filter}
                  isVisible={isVisible}
                  specificFilters={specificFilters}
                  filterPartitionId={idRef.current}
                  hasPermission={hasPermission}
                  {...customComponentProps}
                />
              );
            })()}
        </Surface>
      </Surface>
    </div>
  );
};

export default CustomViewContainer;
